import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ScrollText, Undo2, Loader2 } from "lucide-react";
import { api, ApiError, type ApiResponse } from "@/api/client";

interface AuditRow {
  id: string;
  entityType: string;
  entityId: string;
  action: string;
  actorName: string | null;
  actorUserName: string | null;
  summary: string | null;
  meta?: Record<string, unknown> | null;
  createdAt: string;
}

const ENTITY_TYPES = [
  "order",
  "invoice",
  "payment",
  "document",
  "service_booking",
  "departure",
  "complaint",
  "agent",
  "customer",
  "trip",
  "destination",
  "activity",
];

/** System audit log viewer (Block 3 B3.7) over the append-only audit_log. Read-only, filterable. */
export default function AuditPage() {
  const [entityType, setEntityType] = useState("");
  const [action, setAction] = useState("");
  const [revertingId, setRevertingId] = useState<string | null>(null);
  const [revertedIds, setRevertedIds] = useState<Set<string>>(new Set());
  const [revertNote, setRevertNote] = useState<string | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["audit", entityType, action],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (entityType) qs.set("entityType", entityType);
      if (action) qs.set("action", action);
      qs.set("limit", "200");
      return api.get<{ data: AuditRow[] }>(`/api/admin/audit?${qs.toString()}`);
    },
  });
  const rows = data?.data ?? [];

  // Durable "already reverted" set, derived from the append-only log itself: the revert endpoint
  // writes an `agent_content_edit_reverted` row carrying the proposalId, so this survives a reload
  // (unlike the in-memory revertedIds, which only covers the current session before a refetch).
  const serverReverted = new Set(
    rows
      .filter((r) => r.action === "agent_content_edit_reverted")
      .map((r) => r.meta?.proposalId)
      .filter((p): p is string => typeof p === "string"),
  );

  // Revert a content edit straight from the trail. The audit row carries the proposalId in `meta`;
  // the endpoint restores the before-snapshots for the whole batch (skipping any field changed
  // since). Append-only log → after a successful revert we mark the proposalId done + refetch.
  const revert = async (proposalId: string) => {
    setRevertingId(proposalId);
    setRevertNote(null);
    try {
      const res = await api.post<
        ApiResponse<{ reverted: number; conflicts: { label: string; reason: string }[] }>
      >(`/api/admin/agent/proposals/${proposalId}/revert`);
      setRevertedIds((prev) => new Set(prev).add(proposalId));
      const conflicts = res.data.conflicts ?? [];
      setRevertNote(
        conflicts.length
          ? `Reverted ${res.data.reverted}, skipped ${conflicts.length} (changed since): ${conflicts
              .map((cf) => cf.label)
              .join(", ")}`
          : `Reverted ${res.data.reverted} field(s).`,
      );
      await refetch();
    } catch (err) {
      // Idempotent UX: an "already reverted" race isn't a failure — mark it done, don't alarm.
      if (err instanceof ApiError && err.code === "invalid_state") {
        setRevertedIds((prev) => new Set(prev).add(proposalId));
        setRevertNote("This edit was already reverted.");
      } else {
        setRevertNote(err instanceof Error ? err.message : "Failed to revert");
      }
    } finally {
      setRevertingId(null);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex items-center gap-3">
        <ScrollText className="h-6 w-6 text-gray-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Audit log</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Append-only trail of every status change, payment, document, cancellation, agent action
            and GDPR erasure.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <select
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        >
          <option value="">All entities</option>
          {ENTITY_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <input
          value={action}
          onChange={(e) => setAction(e.target.value)}
          placeholder="Filter by action (e.g. payment, gdpr_forget)"
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        />
      </div>

      {revertNote && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
          {revertNote}
        </p>
      )}

      <div className="card overflow-x-auto p-0">
        <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">Entity</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Actor</th>
              <th className="px-4 py-3">Summary</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                  No audit entries.
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                const proposalId =
                  r.action === "agent_content_edit"
                    ? (r.meta?.proposalId as string | undefined)
                    : undefined;
                return (
                  <tr key={r.id} className="text-gray-700 dark:text-gray-300">
                    <td className="whitespace-nowrap px-4 py-2 text-gray-500">
                      {new Date(r.createdAt).toLocaleString()}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2">
                      <span className="font-medium">{r.entityType}</span>
                      <span className="ml-1 font-mono text-xs text-gray-400">
                        {r.entityId.slice(0, 12)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 font-mono text-xs">{r.action}</td>
                    <td className="whitespace-nowrap px-4 py-2">
                      {r.actorUserName || r.actorName || "—"}
                    </td>
                    <td className="px-4 py-2 text-gray-500">{r.summary || "—"}</td>
                    <td className="whitespace-nowrap px-4 py-2 text-right">
                      {proposalId &&
                        (revertedIds.has(proposalId) || serverReverted.has(proposalId) ? (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                            <Undo2 className="h-3.5 w-3.5" /> Reverted
                          </span>
                        ) : (
                          <button
                            onClick={() => revert(proposalId)}
                            disabled={revertingId !== null}
                            className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            {revertingId === proposalId ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Undo2 className="h-3.5 w-3.5" />
                            )}
                            Revert
                          </button>
                        ))}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
